import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Login from "../pages/Login";
import Dashboard from "../pages/Dashboard";
import ProtectedRoute from "./ProtectedRoute";
import { ROLES } from "../utils/roles";
import { AuthProvider, useAuth } from "../context/AuthContext";

import Events from "../pages/events";
import EventPayouts from "../pages/payouts";
import CustomFeeDetails from "../pages/custom-fee";
import EventRefundApprovedRequests from "../pages/events-refund-request";
import EventPurchasedTickets from "../pages/events-purchased-tickets";
import UsersList from "../pages/users/index";
import EventBasedGuestList from "../pages/guestList";
import EventPromoCodes from "../pages/promoCodes";
import RefundRequests from "../pages/refunds";
import ScannedTickets from "../pages/scannedTickets";
import EventTicketInfo from "../pages/ticketsInfo";
import MatchedUsers from "../pages/matchedUsers";
import HostList from "../pages/hostList";
import MobileVersioning from "../pages/mobile-versioning";
import TaxManagement from "../pages/tax-management";

/**
 * ProtectedRouteWrapper ensures role + authentication validation
 */
function ProtectedRouteWrapper({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles: string[];
}) {
  const { isAuthenticated, role } = useAuth();

  return (
    <ProtectedRoute
      isAuthenticated={isAuthenticated}
      role={role}
      allowedRoles={allowedRoles}
    >
      {children}
    </ProtectedRoute>
  );
}

/**
 * AppRouter handles all application routing logic
 */
function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-lg font-semibold">
        Loading...
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to="/events" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to="/events" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Login */}
      <Route path="/login" element={<Login />} />

      {/* Dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <Dashboard />
          </ProtectedRouteWrapper>
        }
      />

      {/* Users */}
      <Route
        path="/users"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <UsersList />
          </ProtectedRouteWrapper>
        }
      />

      {/* Events */}
      <Route
        path="/events"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <Events />
          </ProtectedRouteWrapper>
        }
      />

      {/* Payouts */}
      <Route
        path="/events-payouts"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <EventPayouts />
          </ProtectedRouteWrapper>
        }
      />

      {/* Other Protected Pages */}
      <Route
        path="/custom-fee-details"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <CustomFeeDetails />
          </ProtectedRouteWrapper>
        }
      />

      <Route
        path="/events-refund-requests-approved"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <EventRefundApprovedRequests />
          </ProtectedRouteWrapper>
        }
      />

      <Route
        path="/events-purchased-tickets"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <EventPurchasedTickets />
          </ProtectedRouteWrapper>
        }
      />

      <Route
        path="/guest-list"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <EventBasedGuestList />
          </ProtectedRouteWrapper>
        }
      />

      <Route
        path="/promo-codes"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <EventPromoCodes />
          </ProtectedRouteWrapper>
        }
      />

      <Route
        path="/refund-requests"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <RefundRequests />
          </ProtectedRouteWrapper>
        }
      />

      <Route
        path="/scanned-tickets"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <ScannedTickets />
          </ProtectedRouteWrapper>
        }
      />

      <Route
        path="/tickets-info"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <EventTicketInfo />
          </ProtectedRouteWrapper>
        }
      />


      <Route
        path="/matched-requested-users"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <MatchedUsers />
          </ProtectedRouteWrapper>
        }
      />


       <Route
        path="/refunds"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <RefundRequests />
          </ProtectedRouteWrapper>
        }
      />

      <Route
        path="/host-list"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <HostList />
          </ProtectedRouteWrapper>
        }
      />

      <Route
        path="/mobile-versioning"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <MobileVersioning />
          </ProtectedRouteWrapper>
        }
      />

      <Route
        path="/tax-management"
        element={
          <ProtectedRouteWrapper
            allowedRoles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}
          >
            <TaxManagement />
          </ProtectedRouteWrapper>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Wrap everything with AuthProvider
 */
export default function AppRouter() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
